// Option B — the symptoms a clinician must not scroll past.
//
// Roughly 500 checklist ticks are recorded per participant and none of them
// reached a doctor-facing screen before this. Urgent items sit first and are
// never collapsed.
import React from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ShieldCheck } from "lucide-react";
import { TIER_META } from "@/config/clinical";

export default function RedFlagBand({ flags, stage }) {
  const { t } = useTranslation();
  const urgent = flags.filter((f) => f.tier === "urgent");
  const caution = flags.filter((f) => f.tier === "caution");

  if (!flags.length)
    return (
      <div data-testid="red-flag-band"
        className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
        <ShieldCheck className="h-4 w-4 flex-shrink-0" />
        {t("No red-flag symptoms recorded in this assessment.")}
      </div>
    );

  return (
    <div data-testid="red-flag-band" className="space-y-3">
      {[["urgent", urgent], ["caution", caution]].map(([tier, list]) => {
        if (!list.length) return null;
        const m = TIER_META[tier];
        return (
          <div key={tier} className={`rounded-lg border ${m.ring} ${m.panel}`} data-testid={`flags-${tier}`}>
            <div className="flex items-center gap-2 px-4 pt-3">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" style={{ color: m.dot }} />
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${m.chip}`}>
                {t(m.label)}
              </span>
              <span className={`text-sm font-semibold ${m.text}`}>
                {list.length} {t(list.length === 1 ? "finding" : "findings")}
              </span>
              {stage && <span className="text-xs text-stone-500">· {t(stage)}</span>}
            </div>
            <ul className="divide-y divide-white/70 px-4 pb-3 pt-2">
              {list.map((f) => (
                <li key={`${f.wid}-${f.index}`} className="py-2" data-testid={`flag-${f.wid}-${f.index}`}>
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className={`text-sm font-semibold ${m.text}`}>{t(f.label)}</span>
                    <span className="font-mono text-[11px] text-stone-500">
                      {t("Worksheet")} {f.worksheetNum}
                    </span>
                  </div>
                  {f.note && (
                    <p className="mt-0.5 text-xs italic text-stone-600">
                      {t("Participant's note")}: “{f.note}”
                    </p>
                  )}
                  <p className="mt-0.5 text-xs text-stone-700">{t(f.action)}</p>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
